import { useState } from 'react';

import { useIsMutating, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { match, P } from 'ts-pattern';

import { contactProfileOptions, mutationKeys } from '@/entities/client';
import { useDiscardChangesGuard } from '@/shared/lib/use-discard-changes-guard';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { SheetBreadcrumb } from '@/shared/ui/panels/sheet-breadcrumb';
import {
    SheetCloseButton,
    SheetDescription,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

import { EditContactProfileForm } from './edit-contact-profile/edit-contact-profile-form';

export function EditContactProfile() {
    const currentSheet = useSheetStore((state) => state.currentSheet);
    const closeCurrentSheet = useSheetStore((state) => state.closeCurrentSheet);

    const isOpen = currentSheet?.type === 'clients/edit-contact-profile';
    const openContact = isOpen ? currentSheet.data : undefined;

    // The store drops the contact the instant the sheet is left, but Radix keeps the content mounted
    // for the slide-out. Holding the last contact keeps the body from flipping to the spinner while
    // it animates away; the query stays disabled once closed, so nothing refetches meanwhile.
    const [retainedContact, setRetainedContact] = useState(openContact);
    if (openContact && openContact.personId !== retainedContact?.personId) {
        setRetainedContact(openContact);
    }
    const contact = openContact ?? retainedContact;

    const profileQuery = useQuery({
        ...contactProfileOptions(contact?.personId ?? ''),
        enabled: isOpen && !!contact?.personId,
    });
    const name = profileQuery.data
        ? `${profileQuery.data.firstName} ${profileQuery.data.lastName}`
        : contact?.name;

    const isSaving =
        useIsMutating({ mutationKey: mutationKeys.updateContactProfile(contact?.personId ?? '') }) >
        0;

    const { confirmDiscardChanges, setDirty } = useDiscardChangesGuard(isSaving);

    const handleClose = () => confirmDiscardChanges(() => closeCurrentSheet());

    return (
        <SheetPanel
            type="clients/edit-contact-profile"
            side="right"
            size="medium"
            showCloseButton={false}
            onRequestClose={handleClose}
        >
            <SheetHeader variant="breadcrumbs">
                <SheetBreadcrumb
                    startAt="clients/contact-details"
                    getLabel={(sheet) =>
                        sheet.type === 'clients/contact-details' &&
                        sheet.data.personId === contact?.personId
                            ? name
                            : undefined
                    }
                    onNavigate={confirmDiscardChanges}
                />
                <SheetCloseButton onClick={handleClose} className="ml-auto shrink-0" />
            </SheetHeader>
            <div className="shrink-0 border-b px-4 py-4">
                <SheetTitle className="font-bold">Edit profile</SheetTitle>
                <SheetDescription className="sr-only">Edit profile for {name}.</SheetDescription>
            </div>
            {match({ profile: profileQuery.data, contact, isError: profileQuery.isError })
                .with(
                    { profile: P.nonNullable, contact: P.nonNullable },
                    ({ profile, contact: shown }) => (
                        <EditContactProfileForm
                            key={shown.personId}
                            profile={profile}
                            onDirtyChange={setDirty}
                        />
                    ),
                )
                .with({ isError: true }, () => (
                    <div className="flex flex-1 items-center justify-center px-4">
                        <p className="text-sm text-muted-foreground">
                            Unable to load this contact's profile. Try again later.
                        </p>
                    </div>
                ))
                .otherwise(() => (
                    <div className="flex flex-1 items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ))}
        </SheetPanel>
    );
}
